// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Mail, Shield, ArrowLeft, Home } from "lucide-react";

const Login = () => {
  const { login, isAuthenticated, loading, requires2FA, verify2FA, cancel2FA } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result?.requires_2fa) {
        toast.info("Please enter your 2FA code");
      } else {
        toast.success("Welcome back!");
        navigate("/admin");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid credentials";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await verify2FA(totpCode);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid 2FA code";
      toast.error(message);
      setTotpCode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setTotpCode("");
    setFormData({ email: "", password: "" });
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Back to Home Link */}
          <Link 
            to="/admin" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/4f9c7ad5-2823-490f-b99e-1d24db3fbcdb/images/112c92c4a088172d03acdf972c6e2e3516082b3829d18e3efd9b6298f1e5a579.png"
                alt="360Wallet"
                className="w-14 h-14"
              />
              <div>
                <h1 className="font-heading font-bold text-2xl bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">360Wallet</h1>
                <p className="text-xs text-muted-foreground">Authorization Engine</p>
              </div>
            </div>
          </div>

          <Card className="bg-card border-border">
            <CardHeader className="space-y-1">
              <CardTitle className="font-heading text-2xl">
                {requires2FA ? "Two-Factor Authentication" : "Admin Portal"}
              </CardTitle>
              <CardDescription>
                {requires2FA 
                  ? "Enter the 6-digit code from your authenticator app"
                  : "Sign in to access the 360Wallet administration console"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {requires2FA ? (
                <form onSubmit={handle2FASubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="totp">Authentication Code</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="totp"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        placeholder="000000"
                        className="pl-10 text-center text-2xl tracking-widest font-mono"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        autoFocus
                        required
                        data-testid="totp-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Or use a backup code if you&apos;ve lost access to your authenticator
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                    disabled={isSubmitting || totpCode.length < 6}
                    data-testid="verify-2fa-btn"
                  >
                    {isSubmitting ? "Verifying..." : "Verify"}
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={handleCancel2FA}
                    data-testid="cancel-2fa-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        className="pl-10"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        autoComplete="off"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="pl-10 pr-10"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        autoComplete="new-password"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full shadow-[0_0_15px_rgba(59,130,246,0.3)] hover:shadow-[0_0_25px_rgba(59,130,246,0.4)] transition-shadow"
                    disabled={isSubmitting}
                    data-testid="login-submit-btn"
                  >
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              )}

              <div className="mt-6 p-3 bg-secondary/30 rounded-lg">
                <p className="text-xs text-muted-foreground text-center">
                  Access is restricted to authorized personnel only.
                  <br />
                  Contact your administrator for account access.
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Motswagae Holding (Pty) Ltd · South Africa
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1581006867287-9f90a3f3a8b0?crop=entropy&cs=srgb&fm=jpg&q=85')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-950/70 to-transparent" />
        
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="max-w-lg animate-slide-up">
            <h2 className="font-heading font-bold text-4xl mb-4">
              Interoperable ATM Withdrawals
            </h2>
            <p className="text-lg text-slate-300 mb-6">
              Enable cross-bank ATM withdrawals from issuer-controlled digital wallets. 
              Authorization, routing, and settlement intelligence in one platform.
            </p>
            <div className="flex gap-6 text-sm">
              <div>
                <p className="text-2xl font-bold text-primary">Multi-Network</p>
                <p className="text-muted-foreground">Interoperability</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-accent">Real-time</p>
                <p className="text-muted-foreground">Authorization</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">Enterprise</p>
                <p className="text-muted-foreground">Security</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
